import { useEffect, useRef, useState } from "react";
import styles from "./ProfileAvatar.module.scss";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { isAxiosError } from "../../../../common/infrastructure/helpers/axiosHelper";
import { uploadImageFile } from "../../../../common/infrastructure/utilities/imageUploadUtility";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";
import { useAuthentication } from "../../../../common/infrastructure/hooks/useAuthentication";
import { Icon, IconType } from "../../../../common/icons/Icon";

export const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #7877c6, #c43f59)",
  "linear-gradient(135deg, #c43f59, #ff8a65)",
  "linear-gradient(135deg, #1bc7c7, #7877c6)",
  "linear-gradient(135deg, #59c9a5, #1bc7c7)",
  "linear-gradient(135deg, #e07b39, #c43f59)",
  "linear-gradient(135deg, #9b59b6, #7877c6)",
];

export function getAvatarGradient(userId: number): string {
  return AVATAR_GRADIENTS[userId % AVATAR_GRADIENTS.length];
}

export function getInitials(firstName: string, lastName?: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() ?? "";
  const last = lastName?.charAt(0)?.toUpperCase() ?? "";
  return first + last || "?";
}

interface IProfileAvatarProps {
  userId: number;
  firstName: string;
  lastName?: string;
  profilePhotoUrl?: string;
  onPhotoUploaded: () => void;
}

export const ProfileAvatar = ({
  userId,
  firstName,
  lastName,
  profilePhotoUrl,
  onPhotoUploaded,
}: IProfileAvatarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(!!profilePhotoUrl);
  const { currentUser } = useAuthentication();

  useEffect(() => {
    setIsImageLoading(!!profilePhotoUrl);
  }, [profilePhotoUrl]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      await uploadImageFile(
        endpointsMetadata.authentication.uploadProfilePhoto,
        file,
        currentUser?.authToken,
      );
      onPhotoUploaded();
    } catch (error) {
      if (isAxiosError(error) && error.response?.data?.message) {
        getErrorToast(error.response.data.message);
      } else {
        getErrorToast("Възникна грешка при качването на снимката.");
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const avatarGradient = getAvatarGradient(userId);
  const initials = getInitials(firstName, lastName);

  return (
    <div className={styles.avatarWrapper}>
      <button
        type="button"
        className={styles.avatar}
        onClick={handleAvatarClick}
        style={!profilePhotoUrl ? { background: avatarGradient } : undefined}
        aria-label="Смяна на профилна снимка"
      >
        {isUploading ? (
          <span className={styles.spinner} />
        ) : profilePhotoUrl ? (
          <>
            <img
              src={profilePhotoUrl}
              alt="Профилна снимка"
              className={styles.avatarImg}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
            {isImageLoading && (
              <span className={`${styles.spinner} ${styles.spinnerOverlay}`} />
            )}
          </>
        ) : (
          <span className={styles.initials}>{initials}</span>
        )}
        <div className={styles.overlay}>
          <Icon type={IconType.Camera} width="20px" height="20px" color="currentColor" />
        </div>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        className={styles.fileInput}
        onChange={handleFileChange}
      />
    </div>
  );
};
